// 제어측 WebRTC 클라이언트. 서버 /ws/viewer 시그널링 + RTCPeerConnection 으로 에이전트 화면(H.264)을
// 수신·렌더하고 입력(input DataChannel)을 전송한다. poc/web-viewer/index.html 의 로직을 TS 클래스로 이식.
// 서버·에이전트 프로토콜(webrtc_offer/answer/ice_candidate, sessions/request)은 기존과 동일.
import { ORG_CONTEXT_HEADER, SERVER, WS_BASE } from './config';
import { USER_SOCKET_KEY, ViewerSocket, lostText } from './viewerSocket';
import { ClipChannel } from './clipboard/channel';
import { CLIP_CHANNEL_LABEL, type ClipCaps } from './clipboard/protocol';
import { FileChannel } from './files/channel';
import { FILE_CHANNEL_LABEL, type FileCaps } from './files/protocol';
import { QualityChannel } from './quality/channel';
import {
  CTRL_CHANNEL_LABEL,
  type QualityApplied,
  type QualityCaps,
} from './quality/protocol';
import { ChatChannel } from './chat/channel';
import { CHAT_CHANNEL_LABEL, type ChatCaps } from './chat/protocol';
import { DrawChannel, type DrawCaps } from './draw/channel';
import { DRAW_CHANNEL_LABEL } from './draw/protocol';

// 공유 신호 소켓의 서버 주소 — viewerSocket.ts 는 config 를 직접 불러오지 않는다(Node 시험용, 그 파일 머리 주석).
ViewerSocket.wsBase = WS_BASE;

export interface RtcStats {
  width: number;
  height: number;
  fps: string;
  kbps: string;
  rttMs: string;
  /**
   * 이 표본 구간의 수신 패킷 손실률(0~1)과 실제 디코딩 fps(수치).
   * 화면에 쓰려는 값이 아니라 **자동 화질 조절(§9.5.4)의 판정 입력**이다 — 그래서 표시용 문자열과 달리
   * 숫자로 둔다. 손실률은 구간 증분으로 계산한다(누적값을 쓰면 세션 초반 한 번의 손실이 영원히 남는다).
   */
  lossPct: number;
  fpsNum: number;
}

/** 원격 모니터 하나의 정보(다중 모니터 전환용). 에이전트가 input 채널로 목록을 보낸다. */
export interface MonitorInfo {
  i: number; // 출력 인덱스(선택 시 그대로 전송)
  x: number;
  y: number;
  w: number;
  h: number;
  primary: boolean;
}

export interface RtcEvents {
  onLog(message: string): void;
  onState(state: string, ok?: boolean | null): void;
  onStream(stream: MediaStream): void;
  onStats(stats: RtcStats): void;
  onClosed(reason: string): void;
  /** 계정 없는 즉석 접속(§17.9.1) 세션의 지속 상한(ms) — 카운트다운 표시용. */
  onAllowedMs?(allowedMs: number): void;
  /** 다중 모니터: 에이전트가 보낸 모니터 목록 + 현재 캡처 인덱스. 목록>1 이면 뷰어가 전환 UI 노출. */
  onMonitors?(list: MonitorInfo[], current: number): void;
  /**
   * 상대 기기가 원격 입력 제어를 허용/차단했다(A4 실시간 스위치).
   *
   * 입력 채널이 열릴 때 1회 + **상태가 바뀔 때마다** 온다. 차단 중에는 마우스·키보드가 상대에게
   * 전달되지 않으므로, 뷰어는 그 이유를 화면에 반드시 표시해야 한다(안 그러면 고장으로 읽힌다).
   */
  onControlAllowed?(allowed: boolean): void;
  /**
   * 클립보드 공유(§9.3.1) 가능 — 에이전트가 clip 채널을 열고 지원 종류·상한을 알렸다.
   * 구버전 에이전트는 이 채널을 만들지 않으므로 이 콜백이 오지 않는다(뷰어는 버튼을 숨기고 단축키를 평소처럼 보낸다).
   */
  onClipReady?(caps: ClipCaps): void;
  /**
   * 파일 전송(§9.4) 가능 — 에이전트가 file 채널을 열고 기능·상한을 알렸다. 구버전 에이전트는 오지 않는다(파일 기능 숨김).
   */
  onFileReady?(caps: FileCaps): void;
  /**
   * 화면 품질·FPS 조절(§9.5) 가능 — 에이전트가 ctrl 채널을 열고 프리셋·FPS 목록을 알렸다.
   * 🔴 이 채널은 **보기 전용 세션에도 온다**(화질은 제어가 아니라 보는 쪽 편의). 구버전 에이전트는 오지 않는다(UI 숨김).
   */
  onQualityReady?(caps: QualityCaps): void;
  /** 화질 변경이 **실제로 적용된** 결과(요청값이 아니다 — 상한·업스케일 금지로 달라질 수 있다). */
  onQualityApplied?(applied: QualityApplied): void;
  /** 화질 변경 실패 사유(사용자 문구). */
  onQualityError?(message: string): void;
  /**
   * 채팅(§9.6) 가능 — 에이전트가 chat 채널을 열고 지원 종류·상한·**표시 수단**을 알렸다.
   * 🔴 화질과 같이 **보기 전용 세션에도 온다**(채팅은 조작이 아니라 말 걸기).
   * `caps.ui === 'none'` 이면 상대에게 표시할 화면이 없다 → 뷰어는 입력을 잠그고 사유를 보여야 한다.
   */
  onChatReady?(caps: ChatCaps): void;
  /** 화면 그리기(§9.8) 채널 준비 — `caps.ui` 로 원격지 그림판 유무를 안다. 보기 전용 세션에도 온다. */
  onDrawReady?(caps: DrawCaps): void;
  /**
   * 접속이 **성립하지 못하고** 끝났다(상대 거절 · 응답 시간 초과). 영상이 오기 전의 실패라 onClosed(세션 종료)와
   * 구분한다 — 상위는 대기 표시("접속 중…")를 반드시 풀고 사유를 보여야 한다.
   * ⚠️ 이 콜백이 없던 때는 거절을 받아도 `onState` 만 불렀는데, 상위는 세션(영상)이 생기기 전이라 그 상태를
   *    담을 곳이 없어 **"접속 중…" 에 영원히 머물렀다**(2026-09-11 사용자 발견).
   */
  onFailed?(message: string): void;
  /** 승인됐고, 기기가 이 계정을 **비밀번호 없이 허용**하고 있다고 알렸다(뷰어는 이때만 기억한다 — trustedDevices.ts). */
  onTrustedViewer?(): void;
}

/**
 * 접속 실패 사유(서버 `session_approval_result.approvalStatus`) → 사용자 문구.
 * 서버 APPROVAL_STATUS(rejected/timeout)와 문자열이 일치해야 한다. 모르는 값(구 서버 등)은 기본 문구.
 */
export const APPROVAL_FAIL_MESSAGE: Readonly<Record<string, string>> = {
  rejected: '접속이 거부되었습니다. 접속 비밀번호를 확인하세요.',
  timeout: '상대 기기가 응답하지 않습니다. 기기가 켜져 있고 인터넷에 연결돼 있는지 확인하세요.',
};
const APPROVAL_FAIL_DEFAULT = '접속이 거부되었습니다.';
/** 세션 종료 때 그리기 끄기를 보낸 뒤 연결을 닫기까지 기다리는 시간(ms) — 끄기가 실제로 나갈 틈(`close()` 주석). */
const DRAW_OFF_FLUSH_MS = 300;

/**
 * 실패한 REST 응답 → 사용자 문구. 서버(NestJS)는 `{message, error, statusCode}` JSON 을 주므로 **`message` 만** 보여 준다.
 * 종전엔 본문 원문을 붙여 탭에 `… (404) {"message":"해당 코드의 기기를 찾을 수 없습니다.",…}` 가 그대로 떴다(2026-09-12 실기).
 * JSON 이 아니거나 message 가 없으면 상태 코드만 붙인 기본 문구.
 */
async function requestFailMessage(r: Response, fallback: string): Promise<string> {
  const text = await r.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { message?: unknown };
    const m = Array.isArray(body.message) ? body.message.join(', ') : body.message;
    if (typeof m === 'string' && m.trim()) {
      return m;
    }
  } catch {
    // JSON 아님 — 기본 문구
  }
  return `${fallback} (${r.status})`;
}

/** 코드(9~11자, 대시 포함) 면 deviceCode, 아니면 agentId(UUID) 로 간주. */
function isDeviceCode(target: string): boolean {
  return /^[0-9-]{9,11}$/.test(target);
}

export class RtcClient {
  /** 서버 시그널링 소켓 — 같은 토큰의 세션들과 **공유**한다(viewerSocket.ts — 탭 여러 개 동시 접속). */
  private sock?: ViewerSocket;
  /** 이 세션 메시지 수신 등록 해제 / 소켓 끊김 통지 해제. */
  private releaseClaim?: () => void;
  private offSockClose?: () => void;
  /** 영상이 한 번이라도 왔는지 — 끊겼을 때 "세션 종료"(onClosed) 와 "접속 실패"(onFailed) 를 가른다. */
  private streamStarted = false;
  private pc?: RTCPeerConnection;
  private inputChannel?: RTCDataChannel;
  /** 클립보드 공유 채널(§9.3.1) — 에이전트가 control 세션에서 만든다. 구버전 에이전트면 없다. */
  private clipChannel?: ClipChannel;
  /** 파일 전송 채널(§9.4) — 에이전트가 control 세션에서 만든다. 구버전 에이전트면 없다. */
  private fileChannel?: FileChannel;
  /** 화면 품질 채널(§9.5) — 에이전트가 **control 과 무관하게** 만든다. 구버전 에이전트면 없다. */
  private qualityChannel?: QualityChannel;
  private chatChannel?: ChatChannel;
  private drawChannel?: DrawChannel;
  private sessionId = '';
  private statsTimer?: number;
  private closed = false;
  /** 접속 실패를 이미 알렸는지(중복 통지 방지 — fail() 참조). */
  private failed = false;
  /**
   * 이 세션에 허용된 기능(§14.19.2 — 서버 승인 통지 `features`). `null` = 모름(옛 서버·아직 승인 전) → 막지 않는다.
   * 화면은 이 목록에 없는 기능의 버튼을 숨긴다(편의 — 강제는 에이전트가 채널을 만들지 않는 것으로 한다).
   */
  grantedFeatures: string[] | null = null;
  /** 계정 없는 즉석 접속(§17.9.1 Phase E) 모드 — 익명 엔드포인트/토큰 사용. */
  private anon = false;

  /**
   * @param orgContext 수퍼유저가 고른 조직(§14.19.1) — 세션 요청·ICE·종료 REST 에 `X-RS-Org` 로 싣는다
   *   (서버가 세션의 조직과 요청 문맥을 맞춰 본다). 일반 사용자·익명은 빈 문자열.
   */
  constructor(
    private token: string,
    private readonly events: RtcEvents,
    private readonly orgContext = '',
  ) {}

  /** 인증 머리 — 토큰 + (수퍼유저면) 조직 문맥. */
  private authHeaders(json = false): Record<string, string> {
    return {
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${this.token}`,
      ...(this.orgContext ? { [ORG_CONTEXT_HEADER]: this.orgContext } : {}),
    };
  }

  private log(m: string) {
    this.events.onLog(m);
  }

  /** 대상(코드/agentId)에 접속을 시작한다. 승인·Offer 수신 후 onStream 으로 영상이 전달된다. */
  /** @param remember "다음부터 묻지 않고 접속" — 이 비밀번호로 승인되면 기기가 이 계정을 비밀번호 없이 허용하도록 요청 */
  async connect(target: string, password?: string, remember = false): Promise<void> {
    this.events.onState('로그인 세션 준비…');
    await this.openWs();
    const sessionId = await this.requestSession(target, password, remember);
    // 응답의 sessionId 로 **즉시** 내 세션을 등록한다. 종전엔 이 값을 버리고 첫 Offer 의 sessionId 를 받아들여,
    // 소켓을 공유하면 남의 세션 Offer 를 가로챌 수 있었다. 등록 전에 온 메시지는 소켓이 보관했다가 넘겨준다.
    this.bindSession(sessionId);
    this.events.onState('요청됨 — 승인/Offer 대기');
  }

  /**
   * 계정 없는 즉석 접속(§17.9.1 Phase E). 로그인 없이 코드+비밀번호로 접속한다.
   * 등록 흐름과 순서가 다르다: 익명 요청으로 **먼저 익명 토큰·세션을 발급**받은 뒤 그 토큰으로 WS 를 연다
   * (등록 흐름은 로그인 토큰이 이미 있어 WS 를 먼저 연다). 에이전트 파이프라인 준비(수백 ms~) 동안 WS 가
   * 먼저 연결되므로 Offer 는 익명 뷰어에게 전달된다.
   */
  async connectAnon(deviceCode: string, password: string): Promise<void> {
    this.anon = true;
    this.events.onState('익명 세션 요청…');
    const r = await fetch(`${SERVER}/api/anon-sessions/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceCode: deviceCode.trim(), password }),
    });
    if (!r.ok) {
      throw new Error(await requestFailMessage(r, '익명 접속 요청 실패'));
    }
    const data = (await r.json()) as {
      anonToken: string;
      sessionId: string;
      allowedMs: number;
      status: string;
    };
    this.token = data.anonToken;
    this.sessionId = data.sessionId; // 익명은 요청 응답에서 세션ID 확정(ICE/close 에 사용)
    this.events.onAllowedMs?.(data.allowedMs);
    this.log(`익명 세션 요청됨 (session=${data.sessionId}, 지속 상한 ${Math.round(data.allowedMs / 60000)}분)`);
    await this.openWs();
    // 서버는 익명 뷰어 WS 가 붙는 순간 이미 끝난 거절을 재전달한다(catch-up) — 등록 전에 와도 소켓이 보관해 넘겨준다.
    this.bindSession(data.sessionId);
    this.events.onState('요청됨 — 승인/Offer 대기');
  }

  /** 공유 시그널링 소켓을 얻어 연다(viewer_online 까지). 준비 뒤 끊기면 세션 종료/접속 실패로 알린다. */
  private async openWs(): Promise<void> {
    // 로그인 세션은 **한 소켓을 공유**한다(탭 여러 개 = 소켓 하나). 익명은 세션마다 서버 키가 달라 토큰이 곧 키다.
    const sock = ViewerSocket.shared(this.anon ? this.token : USER_SOCKET_KEY, this.token);
    sock.acquire();
    this.sock = sock;
    this.offSockClose = sock.onClose((reason) => {
      if (this.closed) {
        return;
      }
      this.log(`/ws/viewer 끊김${reason ? ` (${reason})` : ''}`);
      // 창 상한을 넘어 밀려난 경우(`replaced`)는 네트워크 끊김과 다르게 안내한다.
      const text = lostText(reason);
      if (this.streamStarted) {
        this.events.onClosed(text);
      } else {
        this.fail(text);
      }
    });
    await sock.open();
    this.log('/ws/viewer 연결됨');
  }

  /** 이 세션의 서버 메시지만 받도록 등록한다(공유 소켓 — viewerSocket.ts). */
  private bindSession(sessionId: string): void {
    this.sessionId = sessionId;
    // 요청 응답 단계에서 이미 실패·종료 처리됐으면(서버 즉시 timeout 등) 반납한 소켓에 등록을 남기지 않는다.
    if (this.closed) {
      return;
    }
    this.releaseClaim?.();
    this.releaseClaim = this.sock?.claim(sessionId, (m) => void this.onWsMessage(m));
  }

  /** 접속 요청(REST). @returns 서버가 발급한 sessionId */
  private async requestSession(target: string, password?: string, remember = false): Promise<string> {
    const body: Record<string, unknown> = isDeviceCode(target)
      ? { deviceCode: target }
      : { agentId: target };
    if (password) {
      body.password = password;
      // 비밀번호가 있을 때만 뜻이 있다(기기는 무인 비밀번호로 승인된 요청에서만 허용을 만든다).
      if (remember) body.rememberDevice = true;
    }
    const r = await fetch(`${SERVER}/api/sessions/request`, {
      method: 'POST',
      headers: this.authHeaders(true),
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      throw new Error(await requestFailMessage(r, '접속 요청 실패'));
    }
    const data = (await r.json()) as { sessionId?: string; status?: string };
    if (!data.sessionId) {
      throw new Error('접속 요청 응답에 세션 ID 가 없습니다');
    }
    this.log(`접속 요청 전송 (${isDeviceCode(target) ? '코드' : 'agentId'}=${target}, session=${data.sessionId})`);
    // 상대가 오프라인이면 서버가 즉시 timeout 으로 답한다 — 대기하지 않고 바로 실패로 알린다.
    if (data.status === 'timeout') {
      this.fail(APPROVAL_FAIL_MESSAGE.timeout);
    }
    return data.sessionId;
  }

  /**
   * 접속 실패를 상위에 **한 번만** 알린다. 서버는 거절 결과를 푸시와 재전달(WS 연결 시 catch-up)로
   * 두 번 보낼 수 있다 — 두 번째는 무시한다.
   */
  private fail(message: string) {
    if (this.failed || this.closed) {
      return;
    }
    this.failed = true;
    this.events.onFailed?.(message);
  }

  private send(obj: unknown) {
    this.sock?.send(obj);
  }

  /** 이 세션의 서버 메시지(공유 소켓이 sessionId 로 골라 준 것만 온다). */
  private async onWsMessage(msg: any) {
    switch (msg.type) {
      case 'session_approval_result':
        this.log(`승인 결과: approved=${msg.approved} status=${msg.approvalStatus ?? '?'}`);
        if (!msg.approved) {
          this.events.onState('접속 거부됨', false);
          this.fail(APPROVAL_FAIL_MESSAGE[msg.approvalStatus] ?? APPROVAL_FAIL_DEFAULT);
          break;
        }
        // 허용 기능(§14.19.2) — 영상보다 먼저 온다. 세션 상태는 영상 도착 때 이 값을 읽는다(useSessionManager).
        if (Array.isArray(msg.features)) {
          this.grantedFeatures = msg.features.filter((f: unknown): f is string => typeof f === 'string');
        }
        if (msg.trustedViewer === true) {
          // 기기가 이 계정을 비밀번호 없이 허용하고 있다(서버가 기기 판단을 전한 것) — "다음부터 묻지 않고 접속".
          this.events.onTrustedViewer?.();
        }
        break;
      case 'webrtc_offer':
        await this.onOffer(msg);
        break;
      case 'ice_candidate':
        this.onRemoteIce(msg);
        break;
      case 'session_closed':
        this.log(`세션 종료: ${msg.reason}`);
        this.events.onClosed(msg.reason ?? '세션 종료');
        break;
      case 'error':
        this.log(`서버 오류: ${msg.message}`);
        this.events.onState('오류', false);
        break;
      default:
        break;
    }
  }

  private async onOffer(msg: any) {
    this.log(`Offer 수신 (session=${this.sessionId})`);
    this.events.onState('협상 중…');

    // 서버에서 세션용 ICE(STUN/TURN) 설정을 받아 사용한다. STUN/TURN 이 있어야 실인터넷·NAT·브라우저 mDNS
    // 후보 문제가 해결된다(host-only 는 동일 LAN 에서만). 미구성 시 빈 배열(host-only 폴백).
    const iceServers = await this.fetchIceServers();
    const pc = new RTCPeerConnection({ iceServers });
    this.pc = pc;

    pc.ontrack = (e) => {
      this.streamStarted = true;
      this.log('영상 트랙 수신 → 렌더');
      this.events.onStream(e.streams[0]);
      this.events.onState('연결됨', true);
      this.startStats();
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        // 후보 유형(host/srflx/relay) 진단 로그 — STUN/TURN 동작 확인용.
        const m = /\btyp (\w+)/.exec(e.candidate.candidate);
        if (m) {
          this.log(`ICE 후보: ${m[1]}`);
        }
        this.send({
          type: 'ice_candidate',
          sessionId: this.sessionId,
          candidate: {
            candidate: e.candidate.candidate,
            sdpMid: e.candidate.sdpMid,
            sdpMLineIndex: e.candidate.sdpMLineIndex,
          },
        });
      }
    };
    pc.onconnectionstatechange = () => {
      this.log(`WebRTC: ${pc.connectionState}`);
      // 🔴 매체 경로가 죽었다(상대가 같은 기기에 다른 접속을 받아 이 세션을 버렸거나 네트워크 단절).
      //    종전엔 로그만 남아 **화면이 "연결됨" 인 채 멈췄다** — 탭이 여럿이면 어느 것이 죽었는지 알 수 없다.
      if (pc.connectionState === 'failed' && !this.closed) {
        this.events.onClosed('연결이 끊겼습니다');
      }
    };
    pc.ondatachannel = (e) => {
      this.log(`DataChannel 수신: ${e.channel.label}`);
      if (e.channel.label === 'input') {
        this.inputChannel = e.channel;
        // 에이전트→뷰어 메시지(현재는 모니터 목록 {t:'mons'}). 입력은 뷰어→에이전트 단방향이라 수신만 파싱.
        e.channel.onmessage = (ev) => this.onInputChannelMessage(ev.data);
      } else if (e.channel.label === CLIP_CHANNEL_LABEL) {
        // 입력과 별도 채널 — 수 MB 이미지를 보내는 동안에도 마우스·키가 밀리지 않는다.
        this.clipChannel = new ClipChannel(e.channel, (caps) => this.events.onClipReady?.(caps));
      } else if (e.channel.label === FILE_CHANNEL_LABEL) {
        // 클립보드와도 따로 — GB 단위 전송이 입력·클립보드 뒤에 줄 세우지 않게.
        this.fileChannel = new FileChannel(e.channel, (caps) => this.events.onFileReady?.(caps));
      } else if (e.channel.label === CTRL_CHANNEL_LABEL) {
        // 화면 품질·FPS(§9.5). 다른 채널과 달리 **보기 전용 세션에도** 온다.
        this.qualityChannel = new QualityChannel(
          e.channel,
          (caps) => this.events.onQualityReady?.(caps),
          (applied) => this.events.onQualityApplied?.(applied),
          (message) => this.events.onQualityError?.(message),
        );
      } else if (e.channel.label === CHAT_CHANNEL_LABEL) {
        // 채팅(§9.6). 화질과 같이 **보기 전용 세션에도** 온다 — 조작이 아니라 말 걸기다.
        // 대화 자체는 화면(useChat)이 `setListener` 로 직접 받는다 — 여기서는 능력만 알린다
        // (화면은 세션 객체만 받고 이 콜백에는 닿지 못한다).
        this.chatChannel = new ChatChannel(e.channel, (caps) => this.events.onChatReady?.(caps));
      } else if (e.channel.label === DRAW_CHANNEL_LABEL) {
        // 화면 그리기(§9.8). 채팅과 같이 **보기 전용 세션에도** 온다 — 조작이 아니라 화면 위 표시다.
        // 획은 화면(useDrawing)이 `setListener` 로 직접 받는다 — 여기서는 능력만 알린다.
        this.drawChannel = new DrawChannel(e.channel, (caps) => this.events.onDrawReady?.(caps));
      }
    };

    await pc.setRemoteDescription({ type: 'offer', sdp: msg.sdp });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    this.send({ type: 'webrtc_answer', sessionId: this.sessionId, sdp: answer.sdp });
    this.log('Answer 전송');
  }

  private onRemoteIce(msg: any) {
    if (!this.pc || !msg.candidate) {
      return;
    }
    this.pc.addIceCandidate(msg.candidate).catch((e) => this.log(`addIce 오류: ${e.message}`));
  }

  /**
   * 서버에서 세션용 ICE(STUN/TURN) 설정을 조회한다. 실패 시 host-only 폴백([]).
   * 등록=GET /api/sessions/:id/ice, 익명(§17.9.1)=GET /api/anon-sessions/ice(토큰이 세션 식별).
   */
  private async fetchIceServers(): Promise<RTCIceServer[]> {
    const url = this.anon
      ? `${SERVER}/api/anon-sessions/ice`
      : `${SERVER}/api/sessions/${this.sessionId}/ice`;
    try {
      const r = await fetch(url, { headers: this.authHeaders() });
      if (!r.ok) {
        this.log(`ICE 설정 조회 실패 (${r.status}) → host-only`);
        return [];
      }
      const cfg = (await r.json()) as { iceServers?: RTCIceServer[] };
      const servers = cfg.iceServers ?? [];
      this.log(`ICE 설정 수신: ${servers.length}개 서버(STUN/TURN)`);
      return servers;
    } catch (e) {
      this.log(`ICE 설정 조회 오류: ${(e as Error).message} → host-only`);
      return [];
    }
  }

  /** 입력(마우스/키/휠) 메시지를 input DataChannel 로 전송한다(에이전트가 생성한 채널). */
  sendInput(obj: unknown): void {
    if (this.inputChannel && this.inputChannel.readyState === 'open') {
      this.inputChannel.send(JSON.stringify(obj));
    }
  }

  /** 다중 모니터: 캡처 대상 모니터를 전환 요청한다({t:'mon', i}). 에이전트 캡처 루프가 반영. */
  selectMonitor(index: number): void {
    this.sendInput({ t: 'mon', i: index });
  }

  /** input 채널 수신 메시지 처리 — 모니터 목록 + 원격 입력 제어 허용 상태(그 외 무시). */
  private onInputChannelMessage(data: unknown): void {
    if (typeof data !== 'string') {
      return;
    }
    try {
      const v = JSON.parse(data) as {
        t?: string;
        list?: MonitorInfo[];
        cur?: number;
        allowed?: boolean;
      };
      if (v.t === 'mons' && Array.isArray(v.list)) {
        this.events.onMonitors?.(v.list, v.cur ?? 0);
      }
      // 피제어 사용자가 "원격 입력 제어 허용" 을 끄면 에이전트가 입력을 버린다.
      // 그 사실을 말해 주지 않으면 여기서는 **그냥 마우스가 안 먹는 것**으로 보여
      // "프로그램이 멈췄다" 로 읽히고, 세션을 다시 맺는 헛수고로 이어진다.
      if (v.t === 'ctl' && typeof v.allowed === 'boolean') {
        this.events.onControlAllowed?.(v.allowed);
      }
    } catch {
      // 비-JSON 무시
    }
  }

  get hasInput(): boolean {
    return this.inputChannel?.readyState === 'open';
  }

  /** 클립보드 공유 채널(없으면 undefined — 구버전 에이전트·보기 전용 세션). */
  get clip(): ClipChannel | undefined {
    return this.clipChannel;
  }

  /** 파일 전송 채널(없으면 undefined — 구버전 에이전트·보기 전용 세션·파일 백엔드 없는 기기). */
  get files(): FileChannel | undefined {
    return this.fileChannel;
  }

  /** 화면 품질 채널(없으면 undefined — **구버전 에이전트뿐**. 보기 전용 세션에도 온다). */
  get quality(): QualityChannel | undefined {
    return this.qualityChannel;
  }

  /** 채팅 채널(없으면 undefined — **구버전 에이전트·`RS_AGENT_CHAT=0`**. 보기 전용 세션에도 온다). */
  get chat(): ChatChannel | undefined {
    return this.chatChannel;
  }

  /** 화면 그리기 채널(없으면 undefined — 구버전 에이전트·`RS_AGENT_DRAW=0`. 그래도 뷰어 단독 그리기는 된다). */
  get draw(): DrawChannel | undefined {
    return this.drawChannel;
  }

  private startStats() {
    let prevDecoded = 0;
    let prevTs = 0;
    let prevBytes = 0;
    // 손실률은 **구간 증분**으로 낸다(§9.5.4 자동 조절의 입력). 누적 packetsLost/packetsReceived 를 쓰면
    // 세션 초반 한 번의 손실이 평균에 영원히 남아, 회선이 회복돼도 자동 조절이 화질을 못 올린다.
    let prevLost = 0;
    let prevPackets = 0;
    window.clearInterval(this.statsTimer);
    this.statsTimer = window.setInterval(async () => {
      if (!this.pc) {
        return;
      }
      const stats = await this.pc.getStats();
      let inbound: any;
      let pair: any;
      stats.forEach((r: any) => {
        if (r.type === 'inbound-rtp' && r.kind === 'video') inbound = r;
        if (r.type === 'candidate-pair' && r.state === 'succeeded' && r.nominated) pair = r;
      });
      if (!inbound) {
        return;
      }
      const now = inbound.timestamp;
      const dts = prevTs ? (now - prevTs) / 1000 : 0;
      const fpsNum = dts > 0 ? (inbound.framesDecoded - prevDecoded) / dts : 0;
      const fps = dts > 0 ? fpsNum.toFixed(1) : '—';
      const kbps = dts > 0 ? (((inbound.bytesReceived - prevBytes) * 8) / 1000 / dts).toFixed(0) : '—';
      // 이 구간에 새로 잃은 패킷 / 이 구간에 왔어야 할 패킷.
      const lostNow = inbound.packetsLost ?? 0;
      const recvNow = inbound.packetsReceived ?? 0;
      const dLost = Math.max(0, lostNow - prevLost);
      const dRecv = Math.max(0, recvNow - prevPackets);
      const lossPct = dLost + dRecv > 0 ? dLost / (dLost + dRecv) : 0;
      prevDecoded = inbound.framesDecoded;
      prevTs = now;
      prevBytes = inbound.bytesReceived;
      prevLost = lostNow;
      prevPackets = recvNow;
      const rtt =
        pair && pair.currentRoundTripTime != null
          ? (pair.currentRoundTripTime * 1000).toFixed(0)
          : '—';
      this.events.onStats({
        width: inbound.frameWidth ?? 0,
        height: inbound.frameHeight ?? 0,
        fps,
        kbps,
        rttMs: rtt,
        lossPct,
        fpsNum,
      });
    }, 1000);
  }

  /** 세션 종료 및 자원 정리. */
  close(): void {
    if (this.closed) {
      return;
    }
    this.closed = true;
    window.clearInterval(this.statsTimer);
    if (this.sessionId) {
      // 서버에 종료 통지(best-effort). 익명(§17.9.1)은 토큰이 세션을 식별하므로 body 불필요.
      if (this.anon) {
        fetch(`${SERVER}/api/anon-sessions/close`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.token}` },
        }).catch(() => {});
      } else {
        fetch(`${SERVER}/api/sessions/close`, {
          method: 'POST',
          headers: this.authHeaders(true),
          body: JSON.stringify({ sessionId: this.sessionId }),
        }).catch(() => {});
      }
    }
    // 그리기를 켠 채 끝내면 원격지에 끄기를 먼저 보내고, 그 메시지가 나갈 틈을 준 뒤 연결을 닫는다
    // (바로 닫으면 SCTP 가 보내기 전에 끊겨 끄기가 사라질 수 있다 — DrawChannel.closeRemote 주석).
    const pc = this.pc;
    const closePc = () => {
      try {
        pc?.close();
      } catch {
        /* 무시 */
      }
    };
    if (this.drawChannel?.closeRemote()) {
      window.setTimeout(closePc, DRAW_OFF_FLUSH_MS);
    } else {
      closePc();
    }
    // 공유 소켓: 내 등록만 풀고 사용을 반납한다(마지막 세션이면 소켓이 닫힌다 — 다른 탭은 영향 없음).
    this.releaseClaim?.();
    this.offSockClose?.();
    this.sock?.release();
  }
}
